import GuideLayout from "../components/GuideLayout";
import { useLang } from "../i18n/LanguageContext";
import { faqPageLd } from "../lib/schema";

const HERO =
  "https://static.prod-images.emergentagent.com/jobs/c98acbe0-6e23-474e-adcd-5ab166743bcb/images/8f7c7582b0fcbdc86b4b7868aed19ed8be42460faeb9c31e5723a5bb5fd7cda7.jpeg";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Private airport transfer (NCC)",
  name: "Pisa & Florence Airport Transfers",
  provider: { "@id": "https://yourblacklimo.com/#business" },
  areaServed: ["Pisa", "Florence", "Lucca", "Livorno", "Viareggio", "Tuscany"],
  description:
    "Private, chauffeur-driven airport transfers from Pisa (Galileo Galilei) and Florence (Amerigo Vespucci) to hotels, villas, ports and marinas across Tuscany, in a Mercedes V-Class.",
  url: "https://yourblacklimo.com/pisa-florence-airport-transfers",
};

const CONTENT = {
  en: {
    seoTitle: "Pisa & Florence Airport Transfers | Private Chauffeur — Your Black Limo",
    seoDesc:
      "Private chauffeur-driven airport transfers from Pisa (Galileo Galilei) and Florence (Amerigo Vespucci) to your villa, hotel, cruise port or marina in Tuscany. Flight tracking, meet & greet, Mercedes V-Class.",
    overline: "Airport Transfers",
    h1: "Private Airport Transfers — Pisa & Florence",
    intro:
      "A calm, punctual welcome the moment you land. Your chauffeur meets you inside the terminal, carries the luggage and drives you straight to your door — no waiting, no queues, no stress, in a spotless Mercedes V-Class.",
    heroAlt: "Black Mercedes V-Class waiting at the airport terminal for a private transfer",
    blocks: [
      {
        title: "Pisa International Airport",
        body: "Galileo Galilei (PSA) is the most convenient gateway to western Tuscany — minutes from Pisa itself, around 30 minutes to Lucca and about an hour to Florence. We monitor your flight and adjust the pickup automatically for delays, so your driver is always there when you step out.",
      },
      {
        title: "Florence Airport",
        body: "Amerigo Vespucci (FLR) sits close to the heart of Florence and the Chianti hills. From arrivals we take you directly to your hotel in the historic centre, a countryside villa, or onward to the coast — smoothly and on your schedule.",
      },
    ],
    cards: {
      overline: "Where we take you",
      title: "Seamless onward journeys",
      items: [
        { title: "To your Tuscan villa", body: "Door-to-door to villas and agriturismi across Chianti, Lucca, the Val d'Orcia and beyond." },
        { title: "Hotels in Florence & Lucca", body: "Straight to the entrance of your city hotel, luggage handled, no navigating traffic yourself." },
        { title: "Cruise ports & marinas", body: "Direct connections to Livorno, La Spezia and the yacht marinas of Viareggio and Carrara." },
        { title: "Long-distance transfers", body: "Onward to Rome, Milan, the Amalfi Coast or the Italian Lakes in complete comfort." },
      ],
    },
    note: {
      title: "Meet & greet, done properly",
      body:
        "We track your flight in real time and follow every schedule change, so there is never a charge for a delayed arrival and never a driver missing. Your chauffeur waits inside the terminal with a name board, helps with the luggage and guides you to a Mercedes V-Class that seats up to seven guests. Fully licensed NCC, professional English-speaking drivers, child seats on request.",
    },
    cta: {
      title: "Arrange your airport transfer in Tuscany",
      secondaryLabel: "See all services",
      secondaryHref: "/services",
      primaryLabel: "Request a quote",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Questions",
      title: "Frequently asked questions",
      items: [
        { q: "Do you track my flight?", a: "Yes. We monitor your flight in real time and adjust the pickup automatically, so your chauffeur is waiting whether you land early or late." },
        { q: "What happens if my flight is delayed?", a: "Nothing to worry about — we follow every schedule change and there is no extra charge for a delayed arrival." },
        { q: "Where will the driver meet me?", a: "Inside the terminal, at arrivals, holding a name board. Your chauffeur helps with the luggage and walks you to the car." },
        { q: "Do you provide child seats?", a: "Yes, child and booster seats are available free of charge — just let us know the ages when you book." },
      ],
    },
  },
  it: {
    seoTitle: "Transfer Aeroporti Pisa e Firenze | Autista Privato NCC — Your Black Limo",
    seoDesc:
      "Transfer privati con autista dagli aeroporti di Pisa (Galileo Galilei) e Firenze (Amerigo Vespucci) verso villa, hotel, porto crociere o marina in Toscana. Monitoraggio voli, accoglienza in aeroporto, Mercedes Classe V.",
    overline: "Transfer Aeroportuali",
    h1: "Transfer Privati dagli Aeroporti di Pisa e Firenze",
    intro:
      "Un'accoglienza puntuale e serena nel momento in cui atterri. Il tuo autista ti attende all'interno del terminal, si occupa dei bagagli e ti porta direttamente a destinazione — nessuna attesa, nessuna coda, nessuno stress, a bordo di un'impeccabile Mercedes Classe V.",
    heroAlt: "Mercedes Classe V nera in attesa al terminal dell'aeroporto per un transfer privato",
    blocks: [
      {
        title: "Aeroporto di Pisa",
        body: "Il Galileo Galilei (PSA) è l'accesso più comodo alla Toscana occidentale — a pochi minuti da Pisa, circa 30 minuti da Lucca e un'ora circa da Firenze. Monitoriamo il tuo volo e adattiamo automaticamente l'orario del prelievo in caso di ritardo, così il tuo autista è sempre lì quando esci.",
      },
      {
        title: "Aeroporto di Firenze",
        body: "L'Amerigo Vespucci (FLR) è vicinissimo al cuore di Firenze e alle colline del Chianti. Dagli arrivi ti portiamo direttamente al tuo hotel nel centro storico, in una villa di campagna o proseguendo verso la costa — con fluidità e secondo i tuoi orari.",
      },
    ],
    cards: {
      overline: "Dove ti portiamo",
      title: "Proseguimenti senza pensieri",
      items: [
        { title: "Alla tua villa in Toscana", body: "Porta a porta verso ville e agriturismi nel Chianti, a Lucca, in Val d'Orcia e oltre." },
        { title: "Hotel a Firenze e Lucca", body: "Direttamente all'ingresso del tuo hotel in città, bagagli gestiti, senza affrontare il traffico." },
        { title: "Porti e marine", body: "Collegamenti diretti a Livorno, La Spezia e alle marine per yacht di Viareggio e Carrara." },
        { title: "Transfer a lunga percorrenza", body: "Proseguimenti verso Roma, Milano, la Costiera Amalfitana o i Laghi in totale comfort." },
      ],
    },
    note: {
      title: "Accoglienza in aeroporto, fatta bene",
      body:
        "Monitoriamo il tuo volo in tempo reale e seguiamo ogni variazione d'orario: nessun sovrapprezzo per un arrivo in ritardo e nessun autista che manca all'appello. Il tuo autista ti attende all'interno del terminal con un cartello, ti aiuta con i bagagli e ti accompagna a una Mercedes Classe V fino a sette ospiti. NCC regolarmente autorizzato, autisti professionisti che parlano inglese, seggiolini per bambini su richiesta.",
    },
    cta: {
      title: "Organizza il tuo transfer aeroportuale in Toscana",
      secondaryLabel: "Tutti i servizi",
      secondaryHref: "/services",
      primaryLabel: "Richiedi un preventivo",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Domande",
      title: "Domande frequenti",
      items: [
        { q: "Monitorate il mio volo?", a: "Sì. Seguiamo il tuo volo in tempo reale e adattiamo automaticamente l'orario del prelievo, così il tuo autista ti aspetta sia che atterri in anticipo sia in ritardo." },
        { q: "Cosa succede se il volo è in ritardo?", a: "Nessun problema — seguiamo ogni variazione d'orario e non c'è alcun sovrapprezzo per un arrivo in ritardo." },
        { q: "Dove mi aspetta l'autista?", a: "All'interno del terminal, agli arrivi, con un cartello con il tuo nome. L'autista ti aiuta con i bagagli e ti accompagna all'auto." },
        { q: "Fornite seggiolini per bambini?", a: "Sì, seggiolini e rialzi sono disponibili gratuitamente — comunicaci le età al momento della prenotazione." },
      ],
    },
  },
};

export default function AirportTransferGuide() {
  const { lang } = useLang();
  const c = CONTENT[lang === "it" ? "it" : "en"];
  const { ["@context"]: _ctx, ...serviceNode } = jsonLd;
  const graphLd = { "@context": "https://schema.org", "@graph": [serviceNode, faqPageLd(c.faq.items)] };
  return (
    <GuideLayout
      testid="airport-transfer-guide"
      faq={c.faq}
      seo={{ title: c.seoTitle, description: c.seoDesc, path: "/pisa-florence-airport-transfers", locale: lang === "it" ? "it_IT" : "en_US", jsonLd: graphLd }}
      overline={c.overline}
      h1={c.h1}
      intro={c.intro}
      heroImage={HERO}
      heroAlt={c.heroAlt}
      blocks={c.blocks}
      cards={c.cards}
      note={c.note}
      cta={c.cta}
    />
  );
}
